import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { listMeets, PaginatedMeetsResponse } from "@/services/meetsApi";

type StageFilter = "" | "itw1" | "itw2";

export default function MeetsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedMeetsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bornes de recette : isoler la fenetre de demonstration (les N passages
  // reels) sans avoir a compter les lignes a la main.
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [stage, setStage] = useState<StageFilter>("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listMeets(navigate, {
          page,
          size,
          created_from: createdFrom || undefined,
          created_to: createdTo || undefined,
          stage: stage || undefined,
        });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, createdFrom, createdTo, stage]);

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Rendez-vous</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Liste des rendez-vous</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select className="border rounded px-2 py-1 text-sm" value={size} onChange={(e) => { setPage(1); setSize(parseInt(e.target.value, 10)); }}>
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 border rounded p-3 bg-gray-50">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Reserve du</label>
              <input
                type="date"
                className="border rounded px-2 py-1 text-sm"
                value={createdFrom}
                onChange={(e) => { setPage(1); setCreatedFrom(e.target.value); }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">au</label>
              <input
                type="date"
                className="border rounded px-2 py-1 text-sm"
                value={createdTo}
                onChange={(e) => { setPage(1); setCreatedTo(e.target.value); }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Entretien</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={stage}
                onChange={(e) => { setPage(1); setStage(e.target.value as StageFilter); }}
              >
                <option value="">Tous</option>
                <option value="itw1">ITW1 (recruteur)</option>
                <option value="itw2">ITW2 (intervieweur)</option>
              </select>
            </div>
            {(createdFrom || createdTo || stage) && (
              <Button
                variant="outline"
                onClick={() => { setPage(1); setCreatedFrom(""); setCreatedTo(""); setStage(""); }}
              >
                Reinitialiser
              </Button>
            )}
          </div>

          {data?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border rounded p-3">
                <div className="text-xs text-gray-600">Rendez-vous reserves</div>
                <div className="text-2xl font-semibold">{data.summary.total}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-600">Pousses dans l&apos;agenda</div>
                <div className="text-2xl font-semibold">
                  {data.summary.with_external_event}
                  <span className="text-sm font-normal text-gray-500"> / {data.summary.total}</span>
                </div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-600">ITW1 (recruteur)</div>
                <div className="text-2xl font-semibold">{data.summary.itw1}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-600">ITW2 (intervieweur)</div>
                <div className="text-2xl font-semibold">{data.summary.itw2}</div>
              </div>
            </div>
          )}

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Entretien</th>
                  <th className="px-3 py-2 text-left">Candidat</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Telephone</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Horaire</th>
                  <th className="px-3 py-2 text-left">Agenda</th>
                  <th className="px-3 py-2 text-left">Lien</th>
                  <th className="px-3 py-2 text-left">Cree le</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="px-3 py-2">{m.id}</td>
                    <td className="px-3 py-2 uppercase text-xs font-medium">{m.stage ?? "-"}</td>
                    <td className="px-3 py-2 font-medium">
                      {m.candidate_firstname || m.candidate_lastname
                        ? `${m.candidate_firstname ?? ""} ${m.candidate_lastname ?? ""}`.trim()
                        : `#${m.candidate_id}`}
                    </td>
                    <td className="px-3 py-2">{m.candidate_email || "-"}</td>
                    <td className="px-3 py-2">{m.candidate_phone || "-"}</td>
                    <td className="px-3 py-2">{m.start_date}</td>
                    <td className="px-3 py-2">{m.start_time?.substring(0, 5)} - {m.end_time?.substring(0, 5)}</td>
                    <td className="px-3 py-2">
                      {m.external_event_synced ? (
                        <span className="text-green-700" title="Evenement present dans l'agenda externe">
                          &#10003; synchronise
                        </span>
                      ) : (
                        <span className="text-gray-400" title="Aucun evenement pousse dans un agenda externe">
                          &#8212;
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {m.meeting_url ? (
                        <a className="text-blue-600 underline" href={m.meeting_url} target="_blank" rel="noreferrer">Lien</a>
                      ) : "-"}
                    </td>
                    <td className="px-3 py-2">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "-"}</td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={10}>Aucun rendez-vous</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedent</Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
